import {
  getLeoUuid,
  LeoUUID,
  getObject,
  getString,
  getLeoTimestamp,
  LeoTimestamp,
} from "@surya-digital/leo-ts-types";
import { BoundedName, BoundedNameDTO } from "../types/boundedName";
import { DeliveryStats, DeliveryStatsDTO } from "./deliveryStats";
import { EmailCycleStatus } from "./emailCycleStatus";

export interface EmailCycleDTO {
  id: string;
  cycleName: BoundedNameDTO;
  createdBy: string;
  createdAt: string;
  modifiedAt: string;
  scheduledAt: string;
  deliveryStats: DeliveryStatsDTO;
  status: EmailCycleStatus.EmailCycleStatusDTO;
}

export class EmailCycle {
  readonly isEmailCycleEmailCycle: boolean = true;

  readonly id: LeoUUID;
  readonly cycleName: BoundedName;
  readonly createdBy: string;
  readonly createdAt: LeoTimestamp;
  readonly modifiedAt: LeoTimestamp;
  readonly scheduledAt: LeoTimestamp;
  readonly deliveryStats: DeliveryStats;
  readonly status: EmailCycleStatus.EmailCycleStatus;


  constructor(
  id: LeoUUID, 
  cycleName: BoundedName, 
  createdBy: string, 
  createdAt: LeoTimestamp, 
  modifiedAt: LeoTimestamp, 
  scheduledAt: LeoTimestamp, 
  deliveryStats: DeliveryStats, 
  status: EmailCycleStatus.EmailCycleStatus
  ) {
    this.id = id;
    this.cycleName = cycleName;
    EmailCycle.validateCreatedBy(createdBy);
    this.createdBy = createdBy;
    this.createdAt = createdAt;
    this.modifiedAt = modifiedAt;
    this.scheduledAt = scheduledAt;
    this.deliveryStats = deliveryStats;
    this.status = status;
  }

  static fromDTO(dto: EmailCycleDTO): EmailCycle {
    const id = getLeoUuid(dto, "id", InvalidEmailCycleIdError);
    const cycleName = BoundedName.fromDTO(getObject(dto, "cycleName", InvalidEmailCycleCycleNameError));
    const createdBy = getString(dto, "createdBy", InvalidEmailCycleCreatedByError);
    const createdAt = getLeoTimestamp(dto, "createdAt", InvalidEmailCycleCreatedAtError);
    const modifiedAt = getLeoTimestamp(dto, "modifiedAt", InvalidEmailCycleModifiedAtError);
    const scheduledAt = getLeoTimestamp(dto, "scheduledAt", InvalidEmailCycleScheduledAtError);
    const deliveryStats = DeliveryStats.fromDTO(getObject(dto, "deliveryStats", InvalidEmailCycleDeliveryStatsError));
    const status = EmailCycleStatus.fromDTO(getObject(dto, "status", InvalidEmailCycleStatusError));
    return new EmailCycle(
      id,
      cycleName,
      createdBy,
      createdAt,
      modifiedAt,
      scheduledAt,
      deliveryStats,
      status
    );
  }

  toDTO(): EmailCycleDTO {
    const dto: EmailCycleDTO = {
        id: this.id.toDTO(),
        cycleName: this.cycleName.toDTO(),
        createdBy: this.createdBy,
        createdAt: this.createdAt.toDTO(),
        modifiedAt: this.modifiedAt.toDTO(),
        scheduledAt: this.scheduledAt.toDTO(),
        deliveryStats: this.deliveryStats.toDTO(),
        status: EmailCycleStatus.toDTO(this.status)
    };
    return dto;
  }
  private static validateCreatedBy = (createdBy: string): void => {
    if(createdBy.trim().length === 0) {
      throw new InvalidEmailCycleCreatedByNonEmptyError(
        `Attribute createdBy cannot be an empty string.`
      );
    }
  }
  
}

EmailCycle.prototype.toString = function toString(): string {
  return `EmailCycle(id='${this.id}',cycleName='${this.cycleName}',createdBy='${this.createdBy}',createdAt='${this.createdAt}',modifiedAt='${this.modifiedAt}',scheduledAt='${this.scheduledAt}',deliveryStats='${this.deliveryStats}',status='${this.status}')`;
};

export class InvalidEmailCycleError extends Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidEmailCycleError";
  }
}
export class InvalidEmailCycleIdError extends InvalidEmailCycleError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidEmailCycleIdError";
  }
}

export class InvalidEmailCycleCycleNameError extends InvalidEmailCycleError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidEmailCycleCycleNameError";
  }
}

export class InvalidEmailCycleCreatedByError extends InvalidEmailCycleError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidEmailCycleCreatedByError";
  }
}

export class InvalidEmailCycleCreatedByNonEmptyError extends InvalidEmailCycleCreatedByError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidEmailCycleCreatedByNonEmptyError";
  }
}

export class InvalidEmailCycleCreatedAtError extends InvalidEmailCycleError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidEmailCycleCreatedAtError";
  }
}

export class InvalidEmailCycleModifiedAtError extends InvalidEmailCycleError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidEmailCycleModifiedAtError";
  }
}

export class InvalidEmailCycleScheduledAtError extends InvalidEmailCycleError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidEmailCycleScheduledAtError";
  }
}

export class InvalidEmailCycleDeliveryStatsError extends InvalidEmailCycleError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidEmailCycleDeliveryStatsError";
  }
}

export class InvalidEmailCycleStatusError extends InvalidEmailCycleError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidEmailCycleStatusError";
  }
}
